import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Activity, CheckCircle, HelpCircle, ArrowRight, Shield, MessageCircle, Users } from 'react-feather';

const CounsellingServices = () => {
  const handleWhatsAppBooking = () => {
    const message = "Hello Maxma Pharmacy, I would like to book a professional counselling session. Please provide more information on availability and sessions.";
    const whatsappUrl = `https://wa.me/254101757541?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1528716321680-815a8cdb8cbe?q=80&w=1076&auto=format&fit=crop"
            alt="Professional Counselling"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-red-600/40" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-white"
          >
            <div className="inline-flex items-center space-x-2 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-full px-4 py-2 mb-6">
              <MessageCircle className="h-4 w-4 text-red-200" />
              <span className="text-sm font-semibold tracking-wide uppercase">Compassionate Care</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Professional <br />
              <span className="text-red-200">Counselling</span>
            </h1>
            <p className="text-xl text-red-50 mb-8 leading-relaxed">
              A safe, confidential space where you can explore your concerns and work towards positive change with expert support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 -mt-20 relative z-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Info & Services */}
            <div className="lg:col-span-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-12"
              >
                {/* Intro Card */}
                <motion.div variants={itemVariants} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                    <Heart className="mr-4 text-red-500" />
                    How We Support You
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    Our professional counselling services offer support for a range of issues, including general wellbeing and addiction recovery. 
                    We provide a safe, confidential space where you can explore your concerns.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      "Individual sessions for stress & anxiety",
                      "Addiction recovery support",
                      "Relationship & conflict counseling",
                      "Personal development workshops"
                    ].map((service, index) => (
                      <div key={index} className="flex items-start p-4 bg-red-50 rounded-2xl border border-red-100">
                        <CheckCircle className="h-6 w-6 text-red-500 mr-3 flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{service}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Why Choose Us */}
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-red-600 to-red-700 p-8 md:p-12 rounded-3xl shadow-xl text-white relative overflow-hidden">
                  <div className="absolute right-0 bottom-0 opacity-10">
                    <Users size={200} />
                  </div>
                  <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-6">Why Choose Us?</h2>
                    <p className="text-xl text-red-50 leading-relaxed mb-8">
                      Our counselors are experienced professionals dedicated to helping you achieve your personal goals. 
                      We take a compassionate, non-judgmental approach tailored to your needs.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Shield className="h-5 w-5 mr-2" />
                        <span>Strictly Confidential</span>
                      </div>
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <MessageCircle className="h-5 w-5 mr-2" />
                        <span>Expert Guidance</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div variants={itemVariants} className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                    <HelpCircle className="mr-4 text-red-500" />
                    Common Questions
                  </h2>
                  <div className="grid gap-6">
                    {[
                      {
                        q: "Is counselling confidential?",
                        a: "Yes, all our counselling services are strictly confidential. Your privacy is our top priority, and we ensure a safe and secure environment for all clients."
                      },
                      {
                        q: "How do I book a counselling session?",
                        a: "You can book a session by calling us directly, visiting our website, or stopping by our office. We offer flexible scheduling to accommodate your needs."
                      }
                    ].map((faq, index) => (
                      <div key={index} className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors">Q: {faq.q}</h3>
                        <p className="text-gray-600 leading-relaxed">A: {faq.a}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Column: CTA / Sticky Info */}
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="sticky top-24"
              >
                <div className="bg-gray-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/20 blur-3xl -mr-16 -mt-16 rounded-full" />
                  <h3 className="text-2xl font-bold mb-4 relative z-10">Take the first step</h3>
                  <p className="text-gray-400 mb-8 relative z-10">
                    Reach out to us today to schedule a session and start your journey towards a healthier, happier you.
                  </p>
                  <button 
                    onClick={handleWhatsAppBooking}
                    className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-4 rounded-2xl transition-all duration-300 flex items-center justify-center group"
                  >
                    Request a Session
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  <div className="mt-8 pt-8 border-t border-gray-800 space-y-4">
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Users className="h-5 w-5 text-red-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Experience</p>
                        <p className="font-semibold">Professional Counselors</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Shield className="h-5 w-5 text-red-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Privacy</p>
                        <p className="font-semibold">100% Confidential</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default CounsellingServices;
